import { useCallback, useState } from "react"
import { css } from "@emotion/react"
import usePodcastFeed from "../usePodcastFeed"
import EmptyState from "../EmptyState"
import ErrorState from "../ErrorState"
import LoadingState from "../LoadingState"
import PodcastHeader from "../PodcastHeader"
import SelectedEpisode from "./SelectedEpisode"
import EpisodeList from "./EpisodeList"
import type { Episode } from "../types"
import { BORDER_COLOR } from "../utils"

export default function PodcastPlayer({
  feedUrl,
  showPodcastInfo = true,
  blockId,
  pageId,
}: {
  feedUrl: string
  showPodcastInfo: boolean
  blockId: string
  pageId: string
}) {
  const { data, isLoading, isError } = usePodcastFeed(pageId, blockId, feedUrl)
  const [selectedEpisodeOverride, setSelectedEpisodeOverride] =
    useState<Episode | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const selectedEpisode = selectedEpisodeOverride ?? data?.episodes?.[0]

  const handleSelectEpisode = useCallback(
    (episode: Episode) => {
      if (episode.audioUrl === selectedEpisode?.audioUrl) return

      setSelectedEpisodeOverride(episode)
      setIsPlaying(false)
    },
    [selectedEpisode],
  )

  if (isLoading) {
    return <LoadingState />
  }

  if (isError || !data || !feedUrl) {
    return <ErrorState />
  }

  return (
    <article css={styles.wrapper}>
      {showPodcastInfo && (
        <PodcastHeader title={data.title} description={data.description} />
      )}

      <div css={styles.playerCard}>
        {data.episodes.length === 0 ? (
          <EmptyState />
        ) : (
          <div css={styles.row}>
            <div css={styles.leftColumn}>
              {selectedEpisode && (
                <SelectedEpisode
                  episode={selectedEpisode}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />
              )}
            </div>

            <div css={styles.rightColumn}>
              {selectedEpisode && (
                <EpisodeList
                  episodes={data.episodes}
                  selectedEpisode={selectedEpisode}
                  onSelectEpisode={handleSelectEpisode}
                  isPlaying={isPlaying}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </article>
  )
}

const styles = {
  wrapper: css`
    container-type: inline-size;
  `,
  playerCard: css`
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid ${BORDER_COLOR};
  `,
  row: css`
    display: grid;
    grid-template-columns: 1fr;

    @container (min-width: 500px) {
      grid-template-columns: 1fr 1fr;
    }
  `,
  leftColumn: css`
    display: flex;
    flex-direction: column;
    padding: 16px 12px;
    gap: 16px;
    border-bottom: 1px solid ${BORDER_COLOR};

    @container (min-width: 500px) {
      border-bottom: none;
      border-right: 1px solid ${BORDER_COLOR};
    }
  `,
  rightColumn: css`
    max-height: 250px;
    overflow-y: auto;

    @container (min-width: 500px) {
      position: relative;
      max-height: none;
      overflow: hidden;

      > * {
        position: absolute;
        inset: 0;
        overflow-y: auto;
      }
    }
  `,
}
